import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';

function APIKeys() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [apiKey, setAPIKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [requestingNewAPIKey, setRequestingNewAPIKey] = useState(false);
  const [showFullKey, setShowFullKey] = useState(false);

  useEffect(() => {
    const fetch_key = async () => {
      const endpoint = `${API_ENDPOINT}/v1/api-keys/`;
      try {
        setIsLoading(true);
        const response = await axios.get<{ api_key: string }>(endpoint, config);
        setAPIKey(response.data.api_key);
      } catch {
        showMessage("Failed to load API key", 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetch_key();
  }, []);

  const generateKey = async () => {
    setIsLoading(true);
    const endpoint = `${API_ENDPOINT}/v1/api-keys/`;
    try {
      const response = await axios.post<{ api_key: string }>(endpoint, {}, config);
      setAPIKey(response.data.api_key);
      showMessage("Generated a new API key!");
    } catch {
      showMessage("Failed to generate a new API key", 'error');
    } finally {
      setIsLoading(false);
      setRequestingNewAPIKey(false);
    }
  };

  const getObfuscatedKey = (key: string) => {
    return showFullKey ? key : `${key.slice(0, 4)}${'*'.repeat(key.length - 4)}`;
  };

  const copyToClipboard = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    showMessage("API key copied to clipboard");
  };

  return (
    <Box sx={{ margin: 'auto', textAlign: 'center', maxWidth: 500 }}>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <br />
          {apiKey && (
            <TextField
              type="text"
              value={getObfuscatedKey(apiKey)}
              fullWidth
              sx={{ mb: 3 }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showFullKey ? "Hide API key" : "Show API key"}>
                      <IconButton onClick={() => setShowFullKey(!showFullKey)}>
                        {showFullKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy API key">
                      <IconButton onClick={copyToClipboard}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}

          {!apiKey ? (
            <Alert severity="info">You do not have an API key yet. Generate one to enable programmatic access.</Alert>
          ) : (
            <Alert severity="warning">Keep your API key secret. It grants programmatic access to your account.</Alert>
          )}

         
            <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              📘{' '}
              <a
                href="https://docs.parceltracer.app/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 'bold' }}
              >
                View API Documentation
              </a>
            </Typography>

            <Typography variant="body2">
              💬 Need help?{' '}
              <a
                href="mailto:info@parceltracer.app?subject=API%20Integration%20Support"
                style={{ textDecoration: 'none', color: '#1976d2' }}
              >
                Contact support
              </a>
            </Typography>
          </Box>

          <Button
            variant="contained"
            disabled={isLoading}
            onClick={() => {
              apiKey ? setRequestingNewAPIKey(true) : generateKey();
            }}
          >
            Generate a new API key {isLoading && <CircularProgress size={20} />}
          </Button>
        </>
      )}

      {requestingNewAPIKey && (
        <ConfirmAction
          message={''}
          action={generateKey}
          isOpen={true}
          handleClose={() => setRequestingNewAPIKey(false)}
          isSevereAction
          confirmSevereActionTarget={"generate new API key"}
        >
          <Alert severity="warning">
            Generating a new API key will deactivate the existing one. You will need to update any integrations using the current key.
          </Alert>
        </ConfirmAction>
      )}
    </Box>
  );
}

export default APIKeys;
